import React from 'react';
import UserAvatar from '../../../../components/UserAvatar';
import { StatusWantType } from '../../../../services/statusWantService';

const kOrange = '#FF8C08';

// Blue/teal gradient — matches the ring around unread message-request
// avatars in the TikTok inbox screenshot.
const kStatusRingColors = ['#25F4EE', '#005BEA'];

// Want ring — shades of the app's own orange.
const kWantRingColors = ['#FFC168', kOrange];

const kViewedSegmentColor = '#3A3D44';

const kAvatarSize = 58;
const kStrokeWidth = 3;
const kRingGap = 4;
const kOuter = kAvatarSize + kRingGap * 2 + kStrokeWidth * 2;

// The "+" add-status badge.
const kAddBadgeSize = 28;
const kAddBadgeBorder = 2.5;

/**
 * One avatar-and-name ring in the horizontal Status/Want row.
 * Segments already viewed render grey; unseen segments keep the type's
 * gradient color.
 */
export default function StatusWantRing({
  name,
  photoUrl,
  type,
  segments,
  onTap,
  isSelf = false,
  onAddTap = null,
}) {
  const label = type === StatusWantType.status ? 'Status' : 'Want';
  const gradientColors =
    type === StatusWantType.status ? kStatusRingColors : kWantRingColors;

  // Viewing your own post never greys it out for you — force every
  // segment "unviewed" here regardless of what the feed computed.
  const effectiveSegments = isSelf
    ? new Array(segments.length === 0 ? 1 : segments.length).fill(false)
    : segments.length === 0
    ? [false]
    : segments;

  return (
    <div style={{ width: 76, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div
        onClick={onTap}
        style={{
          position: 'relative',
          width: kOuter,
          height: kOuter,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
      >
        <SegmentedRing
          segments={effectiveSegments}
          gradientColors={gradientColors}
          viewedColor={kViewedSegmentColor}
          strokeWidth={kStrokeWidth}
          size={kOuter}
        />

        <div
          style={{
            width: kAvatarSize + 4,
            height: kAvatarSize + 4,
            borderRadius: '50%',
            background: '#08090B',
            padding: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <UserAvatar size={kAvatarSize} imageUrl={photoUrl} name={name} />
        </div>

        {/* Label pill — grey, "Status" or "Want" */}
        <div
          style={{
            position: 'absolute',
            bottom: -6,
            padding: '2px 8px',
            background: '#3A3D44',
            borderRadius: 8,
            border: '1.5px solid #08090B',
            zIndex: 2,
          }}
        >
          <span
            style={{
              color: 'rgba(255,255,255,0.7)',
              fontSize: 9,
              fontWeight: 700,
              textDecoration: 'none',
              lineHeight: 1.2,
            }}
          >
            {label}
          </span>
        </div>

        {/* "+" add-status badge — current user's ring only */}
        {isSelf && onAddTap && (
          <div
            onClick={(e) => {
              e.stopPropagation();
              onAddTap();
            }}
            style={{
              position: 'absolute',
              right: 2,
              bottom: 8,
              width: kAddBadgeSize,
              height: kAddBadgeSize,
              borderRadius: '50%',
              background: kOrange,
              border: `${kAddBadgeBorder}px solid #08090B`,
              boxShadow: `0 0 8px 0.5px rgba(255,140,8,0.35)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              zIndex: 3,
            }}
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 5v14M5 12h14"
                stroke="#000"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
            </svg>
          </div>
        )}
      </div>

      <div style={{ height: 10 }} />

      <div
        style={{
          color: 'rgba(255,255,255,0.7)',
          fontSize: 11,
          fontWeight: 500,
          textDecoration: 'none',
          textAlign: 'center',
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {isSelf ? 'Your Status' : name}
      </div>
    </div>
  );
}

// ── Segmented ring (SVG) ─────────────────────────────────────────────────

function SegmentedRing({
  segments,
  gradientColors,
  viewedColor,
  strokeWidth,
  size,
}) {
  const count = segments.length;

  // Hook MUST run before any early return — React requires the same
  // hook order on every render.
  const gradientId = React.useMemo(
    () => `ringGrad-${Math.random().toString(36).slice(2, 9)}`,
    []
  );

  if (count === 0) return null;

  const cx = size / 2;
  const cy = size / 2;
  const r = (size - strokeWidth) / 2;

  // Small visual gap between segments, WhatsApp-style — none when single.
  const gapDegrees = count === 1 ? 0 : 8;
  const segmentDegrees = 360 / count - gapDegrees;
  // polarToCartesian below already treats 0° as 12 o'clock (it subtracts 90
  // itself), so no extra -90 here — Flutter's canvas needs it, this doesn't.
  const startOffsetDegrees = 0;

  const polarToCartesian = (centerX, centerY, radius, angleDeg) => {
    const angleRad = ((angleDeg - 90) * Math.PI) / 180;
    return {
      x: centerX + radius * Math.cos(angleRad),
      y: centerY + radius * Math.sin(angleRad),
    };
  };

  const describeArc = (startDeg, sweepDeg) => {
    const start = polarToCartesian(cx, cy, r, startDeg);
    const end = polarToCartesian(cx, cy, r, startDeg + sweepDeg);
    const largeArcFlag = sweepDeg <= 180 ? 0 : 1;
    return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`;
  };

  const strokeFor = (viewed) => (viewed ? viewedColor : `url(#${gradientId})`);

  return (
    <svg
      width={size}
      height={size}
      style={{ position: 'absolute', top: 0, left: 0 }}
    >
      <defs>
        {/* userSpaceOnUse so every segment shares one gradient across the
            whole ring, instead of each arc getting its own bounding box */}
        <linearGradient
          id={gradientId}
          gradientUnits="userSpaceOnUse"
          x1="0"
          y1="0"
          x2={size}
          y2={size}
        >
          {gradientColors.map((c, i) => (
            <stop
              key={i}
              offset={`${(i / (gradientColors.length - 1 || 1)) * 100}%`}
              stopColor={c}
            />
          ))}
        </linearGradient>
      </defs>

      {count === 1 ? (
        // A single segment is a full circle — an SVG arc can't draw that
        // (start point === end point), so use a <circle> instead.
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke={strokeFor(segments[0])}
          strokeWidth={strokeWidth}
        />
      ) : (
        segments.map((viewed, i) => {
          const startDeg =
            startOffsetDegrees + i * (360 / count) + gapDegrees / 2;
          return (
            <path
              key={i}
              d={describeArc(startDeg, segmentDegrees)}
              fill="none"
              stroke={strokeFor(viewed)}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
            />
          );
        })
      )}
    </svg>
  );
}